import { ChevronLeft } from 'lucide-react';
import { useCollection } from '../hooks/useCollection';
import PackEnergyTracker from './PackEnergyTracker';

const SetGrid = ({ selectedSeries, onBack, onSetSelect }) => {
  const {
    getSeriesGroups,
    loading,
    getSetPackCount,
    calculateSetCompletion
  } = useCollection();

  if (!selectedSeries) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
        <p className="text-ink-secondary text-sm mb-4">No series selected.</p>
        <button
          onClick={onBack}
          className="bg-navy-dark text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-navy-light transition-colors"
        >
          ← Back to Series
        </button>
      </div>
    );
  }

  const seriesGroups = getSeriesGroups();
  const setsInSeries = seriesGroups[selectedSeries] || [];

  return (
    <div>
      {/* Sub-header */}
      <div className="bg-navy-dark border-b-[3px] border-yellow px-4 md:px-10 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-white/70 hover:text-white text-sm flex-shrink-0"
          >
            <ChevronLeft size={16} />
            Back
          </button>
          <span className="text-white/30">/</span>
          <h2 className="font-heading font-bold text-white text-base md:text-lg truncate">{selectedSeries}</h2>
          {loading && (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-transparent flex-shrink-0"></div>
          )}
        </div>
        <span className="text-[11px] font-medium text-white bg-white/10 px-2.5 py-1 rounded-full flex-shrink-0">
          {setsInSeries.length} sets
        </span>
      </div>

      <div className="px-4 md:px-10 py-6">
        {setsInSeries.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-ink-secondary text-sm">No sets found for {selectedSeries} series.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {setsInSeries.map((set) => {
              const releaseYear = new Date(set.releaseDate).getFullYear();
              const completion = calculateSetCompletion(set.id, set.total);

              return (
                <div
                  key={set.id}
                  onClick={() => onSetSelect(set)}
                  className="bg-white border border-border rounded-2xl overflow-hidden cursor-pointer hover:shadow-card transition-shadow"
                >
                  <div className="h-20 md:h-[88px] bg-cream-card flex items-center justify-center p-3">
                    {set.images?.logo ? (
                      <img
                        src={set.images.logo}
                        alt={`${set.name} logo`}
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <div className="font-heading font-bold text-ink-muted text-xs text-center px-2">
                        {set.name}
                      </div>
                    )}
                  </div>

                  <div className="p-3 md:p-3.5">
                    <h3 className="text-[13px] md:text-[14px] font-bold text-ink-primary mb-0.5 leading-tight">
                      {set.name}
                    </h3>
                    <div className="text-[11px] md:text-[12px] text-ink-secondary mb-2">
                      {set.total} cards • {releaseYear}
                    </div>

                    <div className="mb-2.5">
                      <div className="h-[3px] bg-cream-bg rounded-full overflow-hidden">
                        <div
                          className="h-full bg-yellow transition-[width] duration-400 ease-out"
                          style={{ width: `${completion.percentage}%` }}
                        />
                      </div>
                      <div className="text-[11px] text-ink-muted mt-1">
                        {completion.collected} / {completion.total} cards
                      </div>
                    </div>

                    <PackEnergyTracker set={set} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {setsInSeries.length > 0 && (
          <div className="mt-8 text-center text-sm text-ink-secondary">
            {setsInSeries.length} sets • Total cards across all sets:{' '}
            {setsInSeries.reduce((sum, set) => sum + (set.total || 0), 0).toLocaleString()}
            <br />
            <span className="font-medium text-ink-primary">
              Total packs opened in this series:{' '}
              {setsInSeries.reduce((sum, set) => sum + getSetPackCount(set.id), 0)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SetGrid;
